import { Observable, of, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpResponse, HttpClient } from '@angular/common/http';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { MuniV3Schema, BuysideV1Schema } from './deal.service.types';
import { Deal } from '../types/deal.types';
import { PlatformType } from '../types/muni-environment';
import { BidResultsResponse } from '../state/bid-results/state';

interface DealServiceConfig {
  getDealUrl: (dealId: string) => string;
  getBidsUrl: (dealId: string) => string;
  getBidResultsUrl: (dealId: string) => string;
  mapper: (apiData: any) => Deal;
}

@Injectable()
export class DealService {
  private apiHost: string;
  private platform: string;
  private readonly platforms: { [key: string]: DealServiceConfig } = {
    polaris: {
      mapper: MuniV3Schema.toDeal,
      getDealUrl: (dealId: string) => `${this.apiHost}/api/v3/issues/${dealId}`,
      getBidsUrl: (_dealId: string) => {
        throw new Error(
          'Sellside does not currently support showing bid data!'
        );
      },
      getBidResultsUrl: (dealId: string) =>
        `${this.apiHost}/api/v1/public-deals/${dealId}/bid-results`
    },
    buyside: {
      mapper: BuysideV1Schema.toDeal,
      getDealUrl: (dealId: string) =>
        `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/deal-details`,
      getBidsUrl: (dealId: string) =>
        `${this.apiHost}/v1/deals/fixed-income/municipal/${dealId}/deal-bids`,
      getBidResultsUrl: (dealId: string) =>
        `${this.apiHost}/v1/public-deals/${dealId}/bid-results`
    }
  };
  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig
  ) {
    this.apiHost = environmentConfig.params.apiHost as string;
    this.platform = (environmentConfig.params.platform ||
      'polaris') as PlatformType;
  }

  public getDeal(dealId: string): Observable<Deal> {
    const url = this.config.getDealUrl(dealId);
    const mapper = this.config.mapper;

    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((response: HttpResponse<any>) => {
        const deal = response.status === 200 && response.body;
        return deal
          ? of(mapper(deal))
          : throwError(Error('No such issue by ' + dealId));
      })
    );
  }

  public getBids(dealId: string): Observable<Deal[]> {
    const url = this.config.getBidsUrl(dealId);

    return this.http
      .get(url, {
        observe: 'response'
      })
      .pipe(
        mergeMap((response: HttpResponse<{ bids: Deal[] }>) => {
          if (response.status === 200) {
            const bids = response.body.bids;
            if (this.platform === 'buyside') {
              return of(
                bids.map((bid) => this.config.mapper({ releasedVersion: bid }))
              );
            } else {
              return of(bids.map((bid) => this.config.mapper(bid)));
            }
          } else {
            return throwError(Error('No Bids Available'));
          }
        })
      );
  }

  public getBidResultsResponse(dealId: string): Observable<BidResultsResponse> {
    const url = this.config.getBidResultsUrl(dealId);
    return this.http.get<BidResultsResponse>(url);
  }

  public getBidResults(dealId: string): Observable<BidResultsResponse> {
    const url = this.config.getBidResultsUrl(dealId);
    return this.http.get(url, { observe: 'response' }).pipe(
      mergeMap((res: HttpResponse<BidResultsResponse>) => {
        const data = res.status === 200 && res.body;
        return data
          ? of(data)
          : throwError(Error(`${res.status} ${res.statusText}`));
      })
    );
  }

  private get config() {
    return this.platforms[this.platform];
  }
}
